'use client';

import { useSeries } from "../hooks/useSeries.js";
import { useCartesianContext } from "../context/CartesianProvider/index.js";
import { useColorProcessor } from "../context/PluginProvider/useColorProcessor.js";
import { useStore } from "../internals/store/useStore.js";
import { useSelector } from "../internals/store/useSelector.js";
import { getLabel } from "../internals/getLabel.js";
import { isCartesianSeriesType } from "../internals/isCartesian.js";
import { utcFormatter } from "./utils.js";
import { useXAxis, useYAxis } from "../hooks/useAxis.js";
import { useZAxis } from "../hooks/useZAxis.js";
import { selectorChartsInteractionXAxis, selectorChartsInteractionYAxis } from "../internals/plugins/featurePlugins/useChartInteraction/index.js";
export function useAxisTooltip() {
  const defaultXAxis = useXAxis();
  const defaultYAxis = useYAxis();
  const xAxisHasData = defaultXAxis.data !== undefined && defaultXAxis.data.length !== 0;
  const store = useStore();
  const axisData = useSelector(store, xAxisHasData ? selectorChartsInteractionXAxis : selectorChartsInteractionYAxis);
  const series = useSeries();
  const {
    xAxis,
    yAxis
  } = useCartesianContext();
  const {
    zAxis,
    zAxisIds
  } = useZAxis();
  const colorProcessors = useColorProcessor();
  if (axisData === null) {
    return null;
  }
  const {
    index: dataIndex,
    value: axisValue
  } = axisData;
  const USED_AXIS_ID = xAxisHasData ? defaultXAxis.id : defaultYAxis.id;
  const usedAxis = xAxisHasData ? defaultXAxis : defaultYAxis;
  const relevantSeries = Object.keys(series).filter(isCartesianSeriesType).flatMap(seriesType => {
    const seriesOfType = series[seriesType];
    if (!seriesOfType) {
      return [];
    }
    return seriesOfType.seriesOrder.map(seriesId => {
      const seriesToAdd = seriesOfType.series[seriesId];
      const providedXAxisId = seriesToAdd.xAxisId;
      const providedYAxisId = seriesToAdd.yAxisId;
      const axisKey = xAxisHasData ? providedXAxisId : providedYAxisId;

      // Test if the series uses the default axis
      if (axisKey === undefined || axisKey === USED_AXIS_ID) {
        const xAxisId = providedXAxisId ?? defaultXAxis.id;
        const yAxisId = providedYAxisId ?? defaultYAxis.id;
        const zAxisId = 'zAxisId' in seriesToAdd ? seriesToAdd.zAxisId : zAxisIds[0];
        const color = colorProcessors[seriesType]?.(seriesToAdd, xAxis[xAxisId], yAxis[yAxisId], zAxisId ? zAxis[zAxisId] : undefined)(dataIndex) ?? '';
        const value = seriesToAdd.data[dataIndex] ?? null;
        const formattedValue = seriesToAdd.valueFormatter(value, {
          dataIndex
        });
        const formattedLabel = getLabel(seriesToAdd.label, 'tooltip') ?? null;
        return {
          seriesId,
          color,
          value,
          formattedValue,
          formattedLabel
        };
      }
      return undefined;
    });
  }).filter(item => item != null);
  const axisFormatter = usedAxis.valueFormatter ?? (v => usedAxis.scaleType === 'utc' ? utcFormatter(v) : v.toLocaleString());
  const axisFormattedValue = axisFormatter(axisValue, {
    location: 'tooltip'
  });
  return {
    axisDirection: xAxisHasData ? 'x' : 'y',
    dataIndex,
    seriesItems: relevantSeries,
    axisValue,
    axisFormattedValue
  };
}